<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard headline-id="identifiers" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('identifiers')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('identifiers')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('identifiers')"
                    @enter-edit-mode="onEnterEditMode('identifiers', $t('identifiers'))"
                    @leave-edit-mode="leaveEditMode('identifiers')"
                    @discard-changes="discardChanges('identifiers')"
                    @save="saveForm('identifiers')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('identifiers') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesIdentifiersForm
                        v-if="showForm('identifiers')"
                        ref="projectSeriesIdentifiersFormRef"
                        :series-id="seriesId"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('name-short')"
                            :text="valueToString(series.nameShort)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('name-long')"
                            :text="valueToString(series.nameLong)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('notes')"
                            :text="valueToString(series.notes)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('description')"
                            :text="valueToString(series.description)"
                            :has-border-bottom="true"
                        />
                        <CardRowAttachmentGroup
                            v-if="series.websites && series.websites.links?.nodes.length"
                            :label="$t('other-systems')"
                            :items="toCardRowAttachmentLink(series.websites.links.nodes)"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('project-websites')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="typeDefinitions" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('typeDefinitions')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('typeDefinitions')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('typeDefinitions')"
                    @enter-edit-mode="onEnterEditMode('typeDefinitions', $t('type-definition'))"
                    @leave-edit-mode="leaveEditMode('typeDefinitions')"
                    @discard-changes="discardChanges('typeDefinitions')"
                    @save="saveForm('typeDefinitions')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('type-definition') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesTypeDefinitionForm
                        v-if="showForm('typeDefinitions')"
                        ref="projectSeriesTypeDefinitionFormRef"
                        :series-id="seriesId"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('project-category')"
                            :text="valueToString(series.category)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('project-type')"
                            :text="valueToString(series.type)"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowAttachmentGroup from '@/components/Card/CardRowAttachmentGroup.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import { toCardRowAttachmentLink } from '@/components/MultiLink/multilink';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { $t } from '@/plugins/fluent';
import { useProjectSeriesInjection } from '@/project/composables/useProjectSeriesInjection';
import ProjectSeriesIdentifiersForm from '@/project/forms/series/ProjectSeriesIdentifiersForm.vue';
import ProjectSeriesTypeDefinitionForm from '@/project/forms/series/ProjectSeriesTypeDefinitionForm.vue';
import { useHeadSafe } from '@unhead/vue';
import { useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

defineProps<{ seriesId: string }>();

const { series } = useProjectSeriesInjection();

const {
    activeCard,
    getCardStatus,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    resetActiveCard,
    onEnterEditMode,
} = useActiveCard();

const projectSeriesIdentifiersFormRef = useTemplateRef('projectSeriesIdentifiersFormRef');
const projectSeriesTypeDefinitionFormRef = useTemplateRef('projectSeriesTypeDefinitionFormRef');
const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();
register('identifiers', projectSeriesIdentifiersFormRef);
register('typeDefinitions', projectSeriesTypeDefinitionFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm($t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});

useHeadSafe({ title: () => `${$t('master-data')} - ${series.value?.nameShort} - ${$t('project-wiki')}` });
</script>
